import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { LabelCounter } from 'app/components/Common/LabelCounter'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { AnimatePresence, motion } from 'framer-motion'
import React, { memo } from 'react'
import { Parallax } from 'react-scroll-parallax'

export interface Props {
  counter?: string
  description?: string
  image?: ImageProps
  label?: string
  subtitle?: string
  thumb?: ImageProps
  title?: string
}

export const AdvancedIntro = memo(function AdvancedIntro({
  counter,
  description,
  image,
  label,
  subtitle,
  thumb,
  title,
}: Props) {
  return (
    <Container>
      <Wrapper row wrap stretch space="between">
        <LeftSide>
          {label ? (
            <FadeInUp delay={0.4}>
              <LabelCounter label={label} counter={counter} />
            </FadeInUp>
          ) : null}
          {title ? <Title tag="h3" text={title} /> : null}

          {subtitle ? (
            <FadeInUp delay={0.6}>
              <Subtitle>{subtitle}</Subtitle>
            </FadeInUp>
          ) : null}

          {description ? (
            <FadeInUp delay={0.8}>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}
          <Media greaterThanOrEqual="desktopSmall">
            {thumb ? (
              <AnimatePresence>
                <ThumbBox>
                  <DropDown delay={0.4}>
                    <Image {...thumb} />
                  </DropDown>
                </ThumbBox>
              </AnimatePresence>
            ) : null}
          </Media>
        </LeftSide>

        <RightSide>
          {image ? (
            <AnimatePresence>
              <ImageWrap className="item-image">
                <DropDown>
                  <ImageBox>
                    <Parallax translateY={['-20%', '20%']}>
                      <Image {...image} />
                    </Parallax>
                  </ImageBox>
                </DropDown>
              </ImageWrap>
            </AnimatePresence>
          ) : null}
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  align-content: stretch;
  margin: 0;
  padding: 6.75rem 5vw 9rem;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.variants.primaryDark};
    height: 77.778vh;
    width: 100%;
  }

  @media (max-width: 1199px) {
    padding: 1.5rem 1.5rem 0;

    &:before {
      height: auto;
      bottom: 15rem;
    }
  }
`
const Wrapper = styled(FlexBox)`
  padding: 6rem 7.847vw 0;
  background-color: ${({ theme }) => theme.colors.variants.neutralLight2};

  @media (max-width: 1199px) {
    padding: 3rem 1.5rem 0;
  }
`

const LeftSide = styled.div`
  width: calc(50% - 9.097vw);

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const ImageWrap = styled.div``

const RightSide = styled.div`
  width: 50%;

  div {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .item-image {
    overflow: hidden;
  }

  @media (max-width: 1199px) {
    width: 100%;
    margin: 3rem 0;

    .item-image {
      aspect-ratio: 1.3467;
    }
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  max-width: 38.25rem;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Subtitle = styled.h4`
  ${styles.subtitle}

  font-size: 1rem;
  text-transform: uppercase;
  line-height: 1.25rem;
  margin-top: 0.375rem;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 0.25rem;
  }
`

const Description = styled.div`
  ${styles.description.dark}

  max-width: 38.25rem;
  margin-top: 2.25rem;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 1.5rem;
  }
`

const ImageBox = styled(motion.div)`
  width: 100%;
  overflow: hidden;
  position: relative;

  > div {
    height: 105%;
  }

  img {
    ${styles.img}
  }
`

const ThumbBox = styled(motion.div)`
  width: 100%;
  aspect-ratio: 4/3;
  margin: 5.25rem auto 0;
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}
  }
`
